import { api } from '../lib/api'
import { useGatedBook } from './useGatedBook'
import type { ClubMark } from '../lib/types'

/**
 * The club register, and nothing else: `GET /golf/register`, the ranked ledger
 * of members in good standing.
 *
 * Separate from `useClubhouseBook` — which reads the register AND the feed in
 * one shot because the clubhouse home is one surface — because The Register
 * page shows only the book. Fetching a feed it never renders would be a second
 * gated round-trip bought for nothing.
 *
 * The gate is the same, and so is the state shape, deliberately: both hooks
 * hand `BookGate` exactly `{ isAuthenticated, membershipRequired, error }`, so
 * a page swapping one for the other changes its reads and not its gate.
 * `useGatedBook` IS that gate and documents its states — a 403
 * `membership_required` is NOT an error but the club ANSWERING, and
 * `membershipRequired` is what the screen renders its own plate from. Every
 * clubhouse PAGE stays ungated on purpose so a lapsed member is never trapped;
 * the standing is enforced on the data, not on the door.
 *
 * Fetches only while `enabled` (the viewer is signed in) — a visitor's console
 * gets a sign-in invitation, not a 401.
 */

/**
 * One line of the club register (#register_entry_json).
 *
 * Declared here rather than in `lib/types.ts` deliberately: that file is copied
 * verbatim into the Caddie app by `bin/mobile contract`, and the register is a
 * web clubhouse read the phone never makes.
 *
 * Ordering is the SERVER's — handicap index ascending, unrated members last —
 * and a page that re-sorts this list client-side must not renumber it. A
 * member's rank in the book is a fact about the book, not about the column
 * somebody happens to be sorting by.
 */
export interface RegisterEntry {
  user_id: string
  display_name: string
  club_mark: ClubMark
  /**
   * The RAW number the club struck — `1`, not "No. 001". The membership
   * payload behind `useClubMembership` formats it and this one does not; use
   * `formatMemberNumber` rather than printing it bare.
   */
  member_number: number | null
  tier: string
  /**
   * The standing's NAME, from `Club::Tiers.find` — so a granted standing (a
   * Quartermaster, a demo member) reads as the club names it rather than as
   * its key. The shell's tiers payload carries only the purchasable three, so
   * a page that looked a name up there printed "quartermaster" raw. Null only
   * for a tier the club no longer defines.
   */
  tier_name: string | null
  /** null is a real answer — "Unrated", fewer than three counting rounds. */
  handicap_index: number | null
  /** 'rated' | 'unrated' — the server's own word for the line above. */
  handicap_status: string
  low_handicap_index: number | null
  handicap_updated_at: string | null
  /** Accepted cards on completed rounds this calendar year. 0, never null. */
  rounds_this_season: number
  /** The lowest differential of those cards, or null when there are none. */
  best_differential: number | null
  /**
   * Movement since the member's PREVIOUS handicap revision: positive is an
   * index that went up. null with no previous revision to compare against, and
   * null when either side of the comparison is Unrated — "improved from
   * nothing" is not a measurement.
   */
  index_delta_recent: number | null
}

interface RegisterResponse {
  register: RegisterEntry[]
}

export const REGISTER_PATH = '/golf/register'

/** One empty book, not a fresh one per render — see `useGatedBook`'s note. */
const NO_REGISTER: RegisterEntry[] = []

export function useRegisterBook(enabled: boolean) {
  const book = useGatedBook<RegisterEntry[]>({
    key: REGISTER_PATH,
    enabled,
    read: () => api.get<RegisterResponse>(REGISTER_PATH).then((payload) => payload.register),
    whenFaulty: 'Could not open the club’s book.',
  })

  return {
    register: book.data ?? NO_REGISTER,
    isLoading: book.isLoading,
    error: book.error,
    membershipRequired: book.membershipRequired,
    refresh: book.refresh,
  }
}
